import React from "react";
import { Tabs } from "antd";
import {
  getSelectedCompanyProfiles,
  selectedCompanyHasProfile,
} from "../utils/activityProfiles";
import {
  AppstoreOutlined,
  BuildOutlined,
  FireOutlined,
  HistoryOutlined,
  RocketOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
} from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSelection } from "../SelectionContext";
import useIsMobile from "../hooks/useIsMobile";
import { getProductionTerminology } from "./productionTerminology";
import "./production-mobile.css";

const { TabPane } = Tabs;

const PRODUCTION_EQUIPMENT_VIEW_PERMISSION =
  "Gestion Commerciale.Approvisionnement.Production.Materiels.view";

const getActiveTabKey = (pathname) => {
  if (pathname.startsWith("/production/kitchen")) return "kitchen";
  if (pathname.startsWith("/production/regularization"))
    return "regularization";
  if (pathname.startsWith("/production/equipment")) return "equipment";
  if (pathname.startsWith("/production/history")) return "history";
  if (pathname.startsWith("/production/process")) return "process";
  return "units";
};

const getDefaultProductionPath = ({
  canViewUnits,
  canViewProcess,
  canViewHistory,
  canViewKitchen,
  canViewEquipment,
}) => {
  if (canViewProcess) return "/production/process";
  if (canViewUnits) return "/production/units";
  if (canViewEquipment) return "/production/equipment";
  if (canViewHistory) return "/production/history";
  if (canViewKitchen) return "/production/kitchen";
  return "/acces-refuse";
};

export const ProductionLandingRedirect = () => {
  const { hasPermission, user } = useAuth();
  const { selectedCompany } = useSelection();

  const isRestaurantActivity = selectedCompanyHasProfile(
    user,
    selectedCompany,
    "restaurant"
  );
  const isProductionActivity = selectedCompanyHasProfile(
    user,
    selectedCompany,
    "production"
  );
  const defaultPath = getDefaultProductionPath({
    canViewUnits: hasPermission(
      "Gestion Commerciale.Approvisionnement.Production.Unites.view"
    ),
    canViewProcess: hasPermission(
      "Gestion Commerciale.Approvisionnement.Production.Gerer.view"
    ),
    canViewHistory: hasPermission(
      "Gestion Commerciale.Approvisionnement.Production.Historique.view"
    ),
    canViewKitchen:
      isRestaurantActivity &&
      hasPermission("Gestion Commerciale.Restauration.Cuisine.view"),
    canViewEquipment:
      isProductionActivity && hasPermission(PRODUCTION_EQUIPMENT_VIEW_PERMISSION),
  });

  return <Navigate to={defaultPath} replace />;
};

const ProductionTabsLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const parentOutletContext = useOutletContext();
  const { hasPermission, user } = useAuth();
  const { selectedCompany } = useSelection();
  const isMobile = useIsMobile();

  const isRestaurantActivity = selectedCompanyHasProfile(
    user,
    selectedCompany,
    "restaurant"
  );
  const productionTerminology = getProductionTerminology(
    getSelectedCompanyProfiles(user, selectedCompany)
  );
  const canViewUnits = hasPermission(
    "Gestion Commerciale.Approvisionnement.Production.Unites.view"
  );
  const canViewProcess = hasPermission(
    "Gestion Commerciale.Approvisionnement.Production.Gerer.view"
  );
  const canViewHistory = hasPermission(
    "Gestion Commerciale.Approvisionnement.Production.Historique.view"
  );
  const canRegularize = hasPermission(
    "Gestion Commerciale.Approvisionnement.Production.Gerer.create"
  );
  const canViewKitchen =
    isRestaurantActivity &&
    hasPermission("Gestion Commerciale.Restauration.Cuisine.view");
  const isProductionActivity = selectedCompanyHasProfile(
    user,
    selectedCompany,
    "production"
  );
  const canViewEquipment =
    isProductionActivity && hasPermission(PRODUCTION_EQUIPMENT_VIEW_PERMISSION);

  const availableTabs = React.useMemo(() => {
    const tabs = [];

    if (canViewUnits) {
      tabs.push({
        key: "units",
        icon: <AppstoreOutlined />,
        label: productionTerminology.unitsTabTitle,
      });
    }

    if (canViewProcess) {
      tabs.push({
        key: "process",
        icon: <RocketOutlined />,
        label: productionTerminology.processTabTitle,
      });
    }

    if (canViewKitchen) {
      tabs.push({
        key: "kitchen",
        icon: <FireOutlined />,
        label: productionTerminology.kitchenTabTitle,
      });
    }

    if (canRegularize) {
      tabs.push({
        key: "regularization",
        icon: <ToolOutlined />,
        label: "Régularisation",
      });
    }

    if (canViewEquipment) {
      tabs.push({
        key: "equipment",
        icon: <BuildOutlined />,
        label: "Matériels & amortissements",
      });
    }

    if (canViewHistory) {
      tabs.push({
        key: "history",
        icon: <HistoryOutlined />,
        label: productionTerminology.historyTabTitle,
      });
    }

    return tabs;
  }, [
    canRegularize,
    canViewEquipment,
    canViewHistory,
    canViewKitchen,
    canViewProcess,
    canViewUnits,
    productionTerminology,
  ]);

  const activeTab = React.useMemo(() => {
    const requestedTab = getActiveTabKey(location.pathname);
    const visibleKeys = availableTabs.map((tab) => tab.key);

    if (visibleKeys.includes(requestedTab)) {
      return requestedTab;
    }

    return availableTabs[0]?.key || "units";
  }, [availableTabs, location.pathname]);

  const handleTabChange = (key) => {
    switch (key) {
      case "units":
        navigate("/production/units");
        break;
      case "process":
        navigate("/production/process");
        break;
      case "kitchen":
        navigate("/production/kitchen");
        break;
      case "regularization":
        navigate("/production/regularization");
        break;
      case "equipment":
        navigate("/production/equipment");
        break;
      case "history":
        navigate("/production/history");
        break;
      default:
        navigate(
          getDefaultProductionPath({
            canViewUnits,
            canViewProcess,
            canViewHistory,
            canViewKitchen,
            canViewEquipment,
          })
        );
        break;
    }
  };

  if (!availableTabs.length) {
    return <Navigate to="/acces-refuse" replace />;
  }

  if (isMobile) {
    return (
      <div className="production-mobile-shell">
        <Outlet context={parentOutletContext} />
      </div>
    );
  }

  return (
    <Tabs
      activeKey={activeTab}
      onChange={handleTabChange}
      size="large"
      type="card"
      destroyInactiveTabPane
    >
      {availableTabs.map((tab) => (
        <TabPane
          tab={
            <span>
              {tab.icon} {tab.label}
            </span>
          }
          key={tab.key}
        >
          <Outlet context={parentOutletContext} />
        </TabPane>
      ))}
    </Tabs>
  );
};

export default ProductionTabsLayout;
